import { useState, useEffect } from 'react';
import { getAccessToken } from '../lib/auth-api';

interface SetupModalProps {
  onComplete: (entrepriseId: string, exerciceId: string) => void;
}

const formeOptions = ['EI', 'EURL', 'SASU', 'SAS', 'SARL', 'SA', 'SNC', 'Autre'];

export default function SetupModal({ onComplete }: SetupModalProps) {
  const [raisonSociale, setRaisonSociale] = useState('');
  const [formeJuridique, setFormeJuridique] = useState('EI');
  const [regimeFiscal, setRegimeFiscal] = useState('IS');
  const currentYear = new Date().getFullYear().toString();
  const [annee, setAnnee] = useState<string>(currentYear);
  const [dateDebut, setDateDebut] = useState(`${currentYear}-01-01`);
  const [dateFin, setDateFin] = useState(`${currentYear}-12-31`);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set default dates based on selected year
  useEffect(() => {
    setDateDebut(`${annee}-01-01`);
    setDateFin(`${annee}-12-31`);
  }, [annee]);

  // Prevent modal dismissal via ESC or backdrop click
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation: dates required and proper order
    if (!dateDebut || !dateFin) {
      setError('Veuillez définir les dates de l\'exercice');
      setLoading(false);
      return;
    }
    if (dateDebut >= dateFin) {
      setError('La date de début doit être antérieure à la date de fin');
      setLoading(false);
      return;
    }

    try {
      const token = getAccessToken();
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/onboarding`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          raison_sociale: raisonSociale,
          forme_juridique: formeJuridique,
          regime_fiscal: regimeFiscal,
          exercice: { debut: dateDebut, fin: dateFin },
        }),
      });

      if (!res.ok) throw new Error('Erreur lors de la création');

      const data = await res.json();
      onComplete(data.entreprise.id, data.exercice.id);
    } catch (err: any) {
      setError(err.message || 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">
          Bienvenue sur BilanExpress
        </h2>
        <p className="text-sm text-gray-500 mb-6">
          Quelques informations pour commencer
        </p>

        {error && (
          <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="raison_sociale" className="block text-sm font-medium text-gray-700 mb-1">
              Raison sociale
            </label>
            <input
              id="raison_sociale"
              type="text"
              required
              value={raisonSociale}
              onChange={(e) => setRaisonSociale(e.target.value)}
              placeholder="Ex: SARL MES PRODUITS"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="forme_juridique" className="block text-sm font-medium text-gray-700 mb-1">
              Forme juridique
            </label>
            <select
              id="forme_juridique"
              value={formeJuridique}
              onChange={(e) => setFormeJuridique(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {formeOptions.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="regime_fiscal" className="block text-sm font-medium text-gray-700 mb-1">
              Régime fiscal
            </label>
            <select
              id="regime_fiscal"
              value={regimeFiscal}
              onChange={(e) => setRegimeFiscal(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="IS">IS - Impôt sur les sociétés</option>
              <option value="BIC">IR - Impôt sur le revenu</option>
            </select>
          </div>

          <div>
            <label htmlFor="annee" className="block text-sm font-medium text-gray-700 mb-1">
              Année de l'exercice
            </label>
            <input
              id="annee"
              type="number"
              min="1900"
              max="2100"
              value={annee}
              onChange={(e) => setAnnee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="date_debut" className="block text-sm font-medium text-gray-700 mb-1">
                Date de début
              </label>
              <input
                id="date_debut"
                type="date"
                value={dateDebut}
                onChange={(e) => setDateDebut(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="date_fin" className="block text-sm font-medium text-gray-700 mb-1">
                Date de fin
              </label>
              <input
                id="date_fin"
                type="date"
                value={dateFin}
                onChange={(e) => setDateFin(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !raisonSociale.trim()}
            className="w-full py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Création...' : 'Commencer →'}
          </button>
        </form>
      </div>
    </div>
  );
}
